import { forwardRef, useState, useSyncExternalStore, type ComponentPropsWithoutRef, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import type { UIMessage } from 'ai';
import { actionChipLabels, isFromCall, isLateApprovalContinuation, readSourceMeta } from './source-chip';
import { branchedSummary, readBranchedDelegation, watchedJobId } from './chat-tasks-view';
import { Link, useNavigate } from 'react-router';
import { ArrowClockwise, WarningCircle, CaretLeft, CaretRight, CircleNotch, EyeSlash, Sparkle, GitFork, GitMergeIcon, Copy, Check, Trash, SealCheckIcon, FilePlus, DownloadSimpleIcon, Microphone } from '@phosphor-icons/react';
import { toast } from 'sonner';
import type { BranchInfo } from '~/hooks/queries/use-branches';
import { renderKeyOf } from '~/hooks/reconcile-history';
import { MarkdownRenderer } from '~/components/markdown-renderer';
import { ReasoningBlock } from './reasoning-block';
import { QuestionsCard } from './questions-card';
import { ProposePlanCard } from './propose-plan-card';
import { isProposePlanSuccessShape } from './plan-status';
import { extractResearchBriefing } from './research-briefing';
import { SourcesBlock } from './sources-block';
import { GraphVisualizationCard, type GraphVisualizationOutput } from './graph-visualization-card';
import { AppPublishedCard, type AppPublishedOutput } from './app-published-card';
import { SketchCard, type SketchOutput } from './sketch-card';
import { DeckCard, type DeckOutput } from './deck-card';
import { useCanvas } from '~/features/canvas/canvas-context';
import { isQuietLineTool } from '~/features/canvas/quiet-line';
import { QuietToolLine } from '~/features/canvas/quiet-tool-line';
import { DocumentCard } from '~/features/canvas/document-card';
import { CanvasOpenButton } from '~/features/canvas/canvas-open-button';
import { useCanvasOpenHandler, canvasItemKeyForHref, isPlainLeftClick } from '~/features/canvas/canvas-link';
import { canvasNewTabHref } from '~/features/canvas/canvas-panel';
import { documentWriteOf, isMarkdownOutputFile } from '~/features/canvas/collect-items';
import { GeneratedImageCard, type GeneratedImageOutput } from './generated-image-card';
import { SiteCheckCard } from './site-check-card';
import { isSiteCheckResultShape, type SiteCheckOutput } from './site-check-output';
import { DesktopTaskCard } from './desktop-task-card';
import type { DesktopTaskToolOutput } from './desktop-task-card-model';
import {
  getCustomToolOutputFiles,
  formatOutputFileSize,
  hasSavedOutputFile,
  isImageOutputFile,
  type CustomToolOutputFile,
} from './custom-tool-output-files';
import { attachmentHref, rawFileUrl, readMessageAttachments, type MessageAttachment } from './image-source';
import { useWindowChrome } from '~/features/windows/window-system';
import { useUiRefactoring } from '~/lib/ui-refactoring';
import { openUploadWindow } from '~/features/windows/window-payloads';
import { messageArtifactsMarkdown } from './conversation-markdown';
import {
  EntityShowCard,
  ActivityShowCard,
  ScheduleShowCard,
  TodoShowCard,
  RelationshipPathCard,
  FileShowCard,
  type EntityShowOutput,
  type FileShowOutput,
} from './viz-cards';
import { AmberCautionBanner } from './amber-caution-banner';
import { StoppedTurnNote, isStoppedMessage } from './stopped-turn';
import { SignalCards, hasSignalParts } from './signal-cards';
import {
  buildOrderedContentSegments,
  hasTextReplacingContinueOffer,
  type AnyToolPart,
  type ContentSegment,
  type ToolContentSegment,
} from './ordered-segments';
import { useHiddenMessages } from '~/lib/stores/hidden-messages-store';
import { AudioAnswerButton } from '~/features/voice/audio-answer-button';
import { finalAnswerText } from '~/features/voice/audio-answer-text';
import { humanizeToolName } from '~/lib/humanize-tool-name';
import { settingsPath } from '~/lib/settings-sections';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/accordion';
import { cn } from '~/lib/utils';
import { readDelegationProgress, type DelegationProgressView } from './delegation-progress';
import { DelegationProgressStrip } from './delegation-progress-strip';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { InitialsAvatar } from '~/components/ui/avatar';
import { EddyAvatar } from '~/components/eddy-mark';
import { useUserDisplayName } from '~/hooks/queries/use-tenant';
import { useEnsureChatTaskListed } from '~/hooks/queries/use-chat-tasks';
import { subscribeGaveUpStreamIds, getGaveUpSnapshot } from '~/hooks/resume-retry-policy';
import { useElapsed, formatElapsed } from './use-elapsed';
import { useAgentRoster } from '~/hooks/queries/use-agent-roster';
import { useCustomAgents } from '~/hooks/queries/use-workspace-custom-agents';
import { Tip } from '~/components/ui/tip';
import { formatUsdAsBlocks } from '~/features/limits/compute-blocks';
import { useShowComputeBlocks } from '~/features/limits/compute-blocks-display';
import { toolInputText } from '~/lib/redact-tool-input';
import { reconnectBannerCopy, STREAM_FAILURE_REASONS, type StreamFailureReason } from './reconnect-banner-copy';

const STREAM_FAILURE_REASON_SET: ReadonlySet<string> = new Set(STREAM_FAILURE_REASONS);

export type ChatMessage = UIMessage;

/**
 * Read-out of the optional message-level metadata our chat-proxy attaches
 * to assistant messages (see `chat-proxy.ts` → finish payload). We don't
 * have a strict type on `UIMessage.metadata` because the AI SDK keeps it
 * generic; pull what we need defensively.
 */
function readEddyMeta(message: UIMessage): { used: boolean; sections: string[] } {
  const m = (message as unknown as { metadata?: unknown }).metadata;
  if (!m || typeof m !== 'object') return { used: false, sections: [] };
  const meta = m as { eddyUsed?: boolean; eddySections?: unknown };
  const sections = Array.isArray(meta.eddySections)
    ? (meta.eddySections.filter((s): s is string => typeof s === 'string'))
    : [];
  return { used: meta.eddyUsed === true || sections.length > 0, sections };
}

interface UsageMeta {
  inputTokens?: number;
  outputTokens?: number;
}

interface MessageMeta {
  agent?: string;
  model?: string;
  /** Set only when LiteLLM resolved an alias to a different served model. */
  requestedModel?: string;
  /**
   * True only when the proxy reported a served model id on its chunks, i.e.
   * `model` is the model that actually produced the answer rather than an
   * echo of the requested alias (FRONT-136). Lets the footer show a
   * verification tick the operator can trust.
   */
  modelConfirmed?: boolean;
  usage?: UsageMeta;
  /**
   * FRONT-2670: the turn's estimated USD. `null` = unpriced (live turn);
   * absent = unpriced on reload, or a reply older than cost tracking.
   */
  costUsd?: number | null;
  /**
   * Stream lifecycle for assistant messages (FRONT-93). Backend emits
   * "interrupted" when the row was left at status="streaming" by an
   * incomplete turn. Absent on rows that finished cleanly; the bubble
   * treats absence as "final" and renders nothing extra.
   */
  streamStatus?: 'interrupted';
  streamInterruptedAt?: string;
  /**
   * FRONT-2195: true only when the backend's shared liveness rule reads this
   * still-streaming row as a turn genuinely running right now (as opposed to
   * one whose producer died and the sweeper hasn't caught yet — both render
   * the identical `streamStatus: 'interrupted'` shape). Drives two things:
   * the resume retry/re-attach behavior (`resume-pursuit-controller.ts`,
   * `resume-retry-policy.ts`, `reattach-decision.ts`) and, here, whether the
   * "interrupted" chip below even renders — a row the client is actively
   * reconnected (or reconnecting) to is not "cut short", so it shows no
   * chip at all rather than a stale warning. See `shouldShowInterruptedChip`.
   */
  streamLive?: boolean;
  /**
   * The stream this row would reconnect to (FRONT-773). Present alongside
   * `streamStatus: 'interrupted'` when the row is worth attempting a resume
   * for; the client's own resume-decision logic requires it (see
   * `resume-decision.ts`) — a permanently-dead row the backend has decided
   * can't be recovered carries no streamId at all. Used here only to look up
   * this row's give-up state in the registry's give-up set (by streamId,
   * whichever chat instance gave up — resume-retry-policy.ts).
   */
  streamId?: string;
  /**
   * FRONT-1638: closed, non-leaky reason the runtime derived for why this
   * row is stalled — lets the reconnect give-up banner say something more
   * honest than "ask again to continue" (see reconnect-banner-copy.ts).
   * Absent when no reason landed on the row (5s-tick limitation server-side)
   * or when the raw metadata carried a string outside the local allowlist.
   */
  streamFailureReason?: StreamFailureReason;
}

/**
 * FRONT-2195: the "interrupted" chip means "this reply was cut short and
 * nobody is coming back to finish it" — wrong for a row the client is
 * actively reconnected (or reconnecting) to. `streamLive` is the exact same
 * liveness rule the resume endpoint itself uses (shared with the backend —
 * see `src/agents/streams/liveness.ts` / `stream-lifecycle-meta.ts`), so a
 * row still being resumed shows no chip at all instead of a stale "cut
 * short" warning that would sit there even while the answer keeps painting.
 */
export function shouldShowInterruptedChip(meta: {
  streamStatus?: 'interrupted';
  streamLive?: boolean;
}): boolean {
  return meta.streamStatus === 'interrupted' && meta.streamLive !== true;
}

function readMessageMeta(message: UIMessage): MessageMeta {
  const m = (message as unknown as { metadata?: unknown }).metadata;
  if (!m || typeof m !== 'object') return {};
  const meta = m as {
    agent?: unknown;
    model?: unknown;
    requestedModel?: unknown;
    modelConfirmed?: unknown;
    usage?: unknown;
    costUsd?: unknown;
    streamStatus?: unknown;
    streamInterruptedAt?: unknown;
    streamLive?: unknown;
    streamId?: unknown;
    streamFailureReason?: unknown;
  };
  const out: MessageMeta = {};
  if (typeof meta.agent === 'string') out.agent = meta.agent;
  if (typeof meta.model === 'string') out.model = meta.model;
  if (typeof meta.requestedModel === 'string') out.requestedModel = meta.requestedModel;
  if (typeof meta.modelConfirmed === 'boolean') out.modelConfirmed = meta.modelConfirmed;
  if (meta.usage && typeof meta.usage === 'object') {
    const u = meta.usage as { inputTokens?: unknown; outputTokens?: unknown };
    const usage: UsageMeta = {};
    if (typeof u.inputTokens === 'number') usage.inputTokens = u.inputTokens;
    if (typeof u.outputTokens === 'number') usage.outputTokens = u.outputTokens;
    if (usage.inputTokens != null || usage.outputTokens != null) out.usage = usage;
  }
  if (typeof meta.costUsd === 'number' && Number.isFinite(meta.costUsd)) out.costUsd = meta.costUsd;
  else if (meta.costUsd === null) out.costUsd = null;
  if (meta.streamStatus === 'interrupted') out.streamStatus = 'interrupted';
  if (typeof meta.streamInterruptedAt === 'string') out.streamInterruptedAt = meta.streamInterruptedAt;
  if (typeof meta.streamLive === 'boolean') out.streamLive = meta.streamLive;
  if (typeof meta.streamId === 'string') out.streamId = meta.streamId;
  // Allowlist read (FRONT-1638) against the shared reason list — an
  // unrecognized string is dropped, not passed through. reconnectBannerCopy's
  // own defensive fallback would catch it too, but the point of an allowlist
  // reader is to never carry an unvalidated string past this boundary.
  if (typeof meta.streamFailureReason === 'string' && STREAM_FAILURE_REASON_SET.has(meta.streamFailureReason)) {
    out.streamFailureReason = meta.streamFailureReason as StreamFailureReason;
  }
  return out;
}

/**
 * FRONT-2670: the reply footer's usage text when the org shows compute
 * blocks — "~1.2k CB", or a dash when there is no estimate — with the tokens
 * moved into the hover hint.
 */
export function footerBlocksLabel(meta: {
  usage?: UsageMeta;
  costUsd?: number | null;
}): { text: string; hint: string } {
  const tokens = `${(meta.usage?.inputTokens ?? 0).toLocaleString('en-US')} input · ${(meta.usage?.outputTokens ?? 0).toLocaleString('en-US')} output tokens`;
  if (typeof meta.costUsd === 'number') {
    return { text: formatUsdAsBlocks(meta.costUsd), hint: `Estimated compute for this reply. ${tokens}.` };
  }
  return { text: '—', hint: `No compute estimate for this reply. ${tokens}.` };
}

function formatTokenCount(n: number): string {
  if (n < 1_000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/**
 * Map an eddy-section name to a gem palette. Tints stay in the warm
 * Infinity-Stones family so the "eddy" identity stays legible; the hue
 * shifts so the user can tell at a glance which slice of eddy's identity
 * informed the answer. "general" / unknown fall back to the classic orange.
 */
const SECTION_PALETTES: Record<string, { glow: string; gradient: string }> = {
  identity: {
    glow: '#38bdf8',
    gradient: 'linear-gradient(135deg, #bae6fd 0%, #38bdf8 40%, #0284c7 75%, #075985 100%)',
  },
  preferences: {
    glow: '#a855f7',
    gradient: 'linear-gradient(135deg, #f3e8ff 0%, #c084fc 40%, #9333ea 75%, #6b21a8 100%)',
  },
  learnings: {
    glow: '#f59e0b',
    gradient: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 40%, #ea580c 75%, #9a3412 100%)',
  },
  general: {
    glow: '#f59e0b',
    gradient: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 40%, #ea580c 75%, #9a3412 100%)',
  },
};

function paletteForSections(sections: string[]): { glow: string; gradient: string } {
  // Priority order: learnings > preferences > identity > general. When a
  // single section is reported, that's the gem. When multiple are used,
  // we pick the highest-priority one — a faceted multi-section indicator
  // would have to be a real SVG; not worth the complexity today.
  for (const key of ['learnings', 'preferences', 'identity', 'general'] as const) {
    if (sections.includes(key)) return SECTION_PALETTES[key];
  }
  return SECTION_PALETTES.general;
}

/**
 * Eddy Stone — a tiny orange gem at the top-right of an assistant bubble
 * when the user's "eddy" identity was injected into the prompt for that
 * turn. Marvel's Soul Stone is the orange one; we lean into that to make
 * it instantly recognisable as identity-influence.
 *
 * Construction: a rotated square gives the diamond/gem silhouette; a
 * diagonal gradient suggests two facets; an inner radial highlight reads
 * as polish; a soft outer glow lifts it off the bubble. The whole thing
 * is wrapped in a button so click → the Personality section of Settings
 * (where the user can
 * edit the identity facts the gem represents) — EXCEPT during onboarding
 * (Marko, 2026-07-29 live-test): `/settings` is a real-app route under
 * `AppLayout`, not the pre-app `AuthLayout` shell the day-one chat runs
 * inside, so clicking it there yanks the user clean out of onboarding into
 * Settings. `linkDisabled` swaps the button for an inert span — still
 * hoverable for the tooltip, just not clickable.
 */
function EddyInfluenceDot({ sections, linkDisabled }: { sections: string[]; linkDisabled?: boolean }) {
  const navigate = useNavigate();
  const palette = paletteForSections(sections);
  const sectionLabel =
    sections.length === 0
      ? 'EDDY'
      : sections.length === 1
        ? sections[0]
        : `${sections.length} EDDY sections`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={linkDisabled ? undefined : () => navigate(settingsPath('personality'))}
          aria-label={
            linkDisabled
              ? `EDDY's identity informed this reply — ${sectionLabel}.`
              : `EDDY's identity informed this reply — ${sectionLabel}. Click to edit.`
          }
          className={cn(
            'absolute -top-1.5 -right-1.5 group/gem',
            linkDisabled ? 'cursor-default' : 'cursor-pointer',
          )}
          style={{ width: 14, height: 14 }}
        >
          {/* Outer glow */}
          <span
            className="absolute inset-0 rounded-sm blur-[3px] opacity-70 group-hover/gem:opacity-100 transition-opacity"
            style={{ background: `radial-gradient(circle, ${palette.glow} 0%, ${palette.glow}00 70%)` }}
          />
          {/* The gem itself — rotated diamond, two-facet diagonal gradient,
              inner highlight, and a thin polished rim. */}
          <span
            className="absolute inset-0 rounded-[2px] rotate-45 shadow-[0_0_0_0.5px_rgba(255,255,255,0.35)_inset,0_0_3px_rgba(0,0,0,0.4)] group-hover/gem:scale-110 transition-transform"
            style={{ background: palette.gradient }}
          >
            {/* Specular highlight on the top-left facet. */}
            <span
              className="absolute top-[1px] left-[1px] w-[4px] h-[4px] rounded-full opacity-80"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)' }}
            />
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="flex items-start gap-1.5">
        <Sparkle size={11} weight="fill" className="mt-0.5 shrink-0" style={{ color: palette.glow }} />
        <span>
          EDDY's identity informed this reply.
          {sections.length > 0 && (
            <>
              <br />
              <span className="text-muted-foreground">
                Sections: {sections.join(', ')}
              </span>
            </>
          )}
          {!linkDisabled && (
            <>
              <br />
              <span className="text-muted-foreground">Click to edit your identity facts.</span>
            </>
          )}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

// FRONT-2034: `buildOrderedContentSegments` and its segment types moved to
// the dependency-free `ordered-segments.ts` so the phone surface
// (`pwa-message.tsx`) can share the exact same ordering logic without
// importing this module (imported above; re-exported here so every existing
// call site and test outside this file is unaffected by the move).
export { buildOrderedContentSegments };
export type { ContentSegment, TextContentSegment, ToolContentSegment } from './ordered-segments';

function getToolInfo(
  part: AnyToolPart,
): { name: string; id: string; input?: unknown; output?: unknown; state: string; errorText?: string } {
  if (part.type === 'dynamic-tool') {
    return {
      name: part.toolName,
      id: part.toolCallId,
      input: part.input,
      output: 'output' in part ? part.output : undefined,
      state: part.state,
      // F9 (PR review): an `output-error` part carries its message on
      // `errorText`, never on `output` — dropping it here is why the
      // collapsed row for a failed call used to fall through to the
      // generic "Running..." fallback below instead of the real error.
      errorText: 'errorText' in part ? part.errorText : undefined,
    };
  }
  const p = part as any;
  return {
    name: p.toolName ?? part.type.replace('tool-', ''),
    id: p.toolCallId ?? '',
    input: p.input,
    output: p.output,
    state: p.state ?? 'output-available',
    errorText: p.errorText,
  };
}

/** Tool name of the blocking multiple-choice card this module sinks last. */
const ASK_QUESTIONS_TOOL = 'ask_questions';

/**
 * FRONT-1685. A question card is always the LAST thing in its message.
 *
 * The model habitually closes an `ask_questions` turn with a line like "the
 * questions are waiting for your answers above — tap through those". Before
 * the ordering fix that was invisible, because ALL prose rendered above every
 * tool card regardless. In true document order it lands BELOW the card, where
 * it repeats what the card's own pending state already says and pushes the
 * card up away from the composer.
 *
 * Done here rather than by telling the model not to write it. A prompt rule
 * is probabilistic — it was tried, and the model still wrote the line — and
 * it costs context on every single turn forever. This is deterministic, free,
 * and testable without a browser.
 *
 * It MOVES rather than hides: text emitted after the call still renders, just
 * above the card, so nothing the model said is ever lost. That is also the
 * shape the original report asked for ("they should be below the actual last
 * chat, not above") and the shape the coding surface already has.
 *
 * Scoped to `ask_questions` alone, deliberately. Ordinary tool cards keep
 * strict document order — that is root cause A and this must not undo it.
 * The question card earns the exception by being BLOCKING: once it is on
 * screen the turn is over, so nothing after it is actionable anyway.
 */
export function sinkQuestionCardsLast(
  segments: ContentSegment[],
  isAskQuestions: (seg: ToolContentSegment) => boolean,
): ContentSegment[] {
  const cards = segments.filter(
    (s): s is ToolContentSegment => s.kind === 'tool' && isAskQuestions(s),
  );
  if (cards.length === 0) return segments;

  const rest = segments.filter((s) => !(s.kind === 'tool' && isAskQuestions(s)));
  // Removing a card from the middle can leave two text segments adjacent that
  // were not before. Re-merge them the same way buildOrderedContentSegments
  // does, so the reader gets one bubble rather than two stacked ones.
  const merged: ContentSegment[] = [];
  for (const seg of rest) {
    const prev = merged[merged.length - 1];
    if (seg.kind === 'text' && prev?.kind === 'text') {
      merged[merged.length - 1] = { kind: 'text', text: `${prev.text}\n\n${seg.text}` };
      continue;
    }
    merged.push(seg);
  }
  return [...merged, ...cards];
}

/**
 * FRONT-1685 (root cause B, chat). The QuestionsCard's React key used to
 * embed `message.id`, which `use-agent-chat.ts`'s `onFinish` deliberately
 * swaps from the AI-SDK's synthetic id to the real `chat_messages.id` once
 * the turn completes — remounting the card and wiping whatever the user
 * had typed. Keying on the server-side question-batch id instead survives
 * that swap, since the batch id never changes; `positionalKey` (the old
 * message-id-based key) is kept only as a fallback for the brief window
 * before the tool output carries a batch id yet.
 */
export function questionsCardKey(positionalKey: string, batchId: number | null | undefined): string {
  return batchId != null ? `questions-${batchId}` : positionalKey;
}

/** A `ContentSegment` with its text segments' briefing/error status already
 *  resolved — what the component actually renders from, one-to-one with
 *  `contentSegments`. */
type RenderableSegment =
  | ToolContentSegment
  | { kind: 'text'; displayText: string; isError: boolean; briefingRunId: number | null };

/**
 * FRONT-717: normalized view of a `delegate_to_agent` tool result's telemetry.
 * The orchestrator's delegate result already carries this — we render it as a
 * snackable stats row instead of dumping the raw JSON into the chat.
 */
export interface DelegateTelemetryView {
  outcome: string;
  toolsRun: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  totalMs?: number;
  upstreamCalls?: number;
  repromptTurns?: number;
  approvalRounds?: number;
  responseChars?: number;
  /**
   * FRONT-1169 (FRONT-1094's Eddy-side half): the specialist executed none of
   * the tools forwarded to it, so its reply is not grounded in any tool
   * result. Detected since FRONT-1071 but never shown to anyone.
   */
  toolsUnreachable?: boolean;
  /** FRONT-1918: files that went WITH the task, and files that could not. */
  filesSent?: number;
  filesNotSent?: number;
  /**
   * FRONT-2670: the run's estimated USD. `undefined` = a delegation older
   * than cost tracking; `null` = its model has no price. Never read as 0.
   */
  costUsd?: number | null;
  /** FRONT-2670: only some turns were priced, so `costUsd` is a floor. */
  costPricedPartially?: boolean;
}

/** FRONT-2670: a number is a price, null is "no price", anything else is a run older than cost tracking. */
function readDelegateCost(v: unknown): number | null | undefined {
  if (typeof v === 'number') return v;
  return v === null ? null : undefined;
}

/** The collapsed delegation row's usage: compute with the switch on, tokens off, nothing without tokens. */
function collapsedUsageText(tel: DelegateTelemetryView, showBlocks: boolean): string | null {
  if (!tel.totalTokens) return null;
  if (showBlocks) return delegateBlocks(tel).value;
  return `${formatTokenCount(tel.totalTokens)} tok`;
}

/**
 * FRONT-2670: the compute figure for a delegation and the hover line that
 * explains it. Unpriced and pre-tracking runs both show a dash; the hint
 * says which.
 */
export function delegateBlocks(tel: DelegateTelemetryView): { value: string; hint: string } {
  const inTok = tel.inputTokens ?? 0;
  const outTok = tel.outputTokens ?? 0;
  const tokens = `${(tel.totalTokens ?? inTok + outTok).toLocaleString('en-US')} tokens (${inTok.toLocaleString('en-US')} in · ${outTok.toLocaleString('en-US')} out)`;
  if (tel.costUsd === undefined) {
    return { value: '—', hint: `This run is older than compute tracking. ${tokens}.` };
  }
  if (tel.costUsd === null) {
    return { value: '—', hint: `Not priced yet: this model has no published price. ${tokens}.` };
  }
  return {
    value: formatUsdAsBlocks(tel.costUsd),
    hint: tel.costPricedPartially
      ? `Estimate. Part of this run is not priced yet, so the real figure is higher. ${tokens}.`
      : `Estimate. ${tokens}.`,
  };
}

/** FRONT-1918: the collapsed-row words for files that went with a hand-off. */
function filesSentLabel(count: number | undefined): string | null {
  if (!count) return null;
  return `${count} ${count === 1 ? 'file' : 'files'} sent`;
}

function filesNotSentLabel(count: number | undefined): string | null {
  return count ? `${count} not sent` : null;
}

export function parseDelegateTelemetry(output: unknown): DelegateTelemetryView | null {
  let o: Record<string, unknown> | null = null;
  if (typeof output === 'string') {
    try {
      o = JSON.parse(output) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (output && typeof output === 'object' && !Array.isArray(output)) {
    o = output as Record<string, unknown>;
  }
  if (!o) return null;
  const t = o.telemetry as Record<string, unknown> | undefined;
  if (!t || typeof t !== 'object') return null;
  const usage = (t.usage ?? {}) as Record<string, unknown>;
  const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);
  let response: string | undefined;
  if (typeof o.response === 'string') response = o.response;
  else if (typeof o.unverifiedResponse === 'string') response = o.unverifiedResponse;
  return {
    outcome: typeof t.outcome === 'string' ? t.outcome : 'ok',
    toolsRun: num(t.serverToolEvents) ?? num(o.serverToolEventCount) ?? 0,
    inputTokens: num(usage.inputTokens),
    outputTokens: num(usage.outputTokens),
    totalTokens: num(usage.totalTokens),
    totalMs: num(t.totalMs),
    upstreamCalls: num(t.upstreamCalls),
    repromptTurns: num(t.repromptTurns),
    approvalRounds: num(t.approvalRounds),
    responseChars: response?.length,
    // Read from telemetry (where FRONT-1071 puts it) with the top-level
    // warning string as a fallback, so either shape surfaces the same state.
    toolsUnreachable:
      t.suspectedUnreachableTools === true || typeof o.toolsUnreachableWarning === 'string'
        ? true
        : undefined,
    filesSent: Array.isArray(o.filesSent) && o.filesSent.length > 0 ? o.filesSent.length : undefined,
    filesNotSent: Array.isArray(o.filesNotSent) && o.filesNotSent.length > 0 ? o.filesNotSent.length : undefined,
    costUsd: readDelegateCost(t.costUsd),
    costPricedPartially: t.costPricedPartially === true ? true : undefined,
  };
}

/**
 * The dot on a tool row. Colours follow the same vocabulary as the chat status
 * light (`chat-light.tsx`) so one colour means one thing everywhere:
 * blue = working, green = done, grey = nothing changed, red = failed.
 *
 * FRONT-889: running used to be amber, which is the colour the rest of the app
 * reserves for "Eddy is waiting for YOU". A tool that was merely slow looked
 * identical to one that had stopped and needed an answer — the confusion in
 * the report ("the orange indicator's meaning is unclear"), made worse by a
 * mis-registered autonomy rule that really did leave `update_custom_agent`
 * blocked behind an approval the Policy page had no row for.
 *
 * The phone's tool row (`pwa-message.tsx`) was changed to match in the same
 * job. The Inspector's running rows are still amber-tinted and deliberately
 * left alone here — that is a whole surface's visual language, not a status
 * vocabulary shared with this row, and changing it is a design call.
 */
function statusDotClass(state: {
  isError: boolean;
  isRunning: boolean;
  isNoChange: boolean;
}): string {
  if (state.isError) return 'bg-destructive';
  if (state.isRunning) return 'bg-primary animate-pulse';
  if (state.isNoChange) return 'bg-muted-foreground/50';
  return 'bg-success';
}

function formatMs(ms: number): string {
  if (ms < 1_000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function StatChip({
  value,
  label,
  title,
  testId,
}: {
  value: string;
  label?: string;
  title?: string;
  testId?: string;
}) {
  return (
    <Tip label={title}>
      <span
        data-testid={testId}
        className="inline-flex items-baseline gap-1 rounded-md border border-border bg-background px-1.5 py-0.5"
      >
        <span className="font-medium text-foreground tabular-nums">{value}</span>
        {label && <span className="text-muted-foreground">{label}</span>}
      </span>
    </Tip>
  );
}

/**
 * The four states a finished delegation can be presented as.
 *
 * FRONT-1169 added `unverified` (upstream says "ok", but none of the forwarded
 * tools ran, so the reply is ungrounded) by extending two parallel nested
 * ternaries — one for the colour, one for the wording. A PR review flagged the
 * readability; the deeper problem was that they were two independent chains, so
 * nothing stopped a colour and a label disagreeing. They already had: an
 * unrecognised outcome read "Error" in a neutral grey.
 *
 * Deriving the state once and looking both up from it makes that impossible.
 */
export type DelegateOutcomeState = 'unverified' | 'ok' | 'no-tools' | 'error';

export const OUTCOME_PRESENTATION: Record<
  DelegateOutcomeState,
  { variant: 'success-soft' | 'destructive-soft' | 'secondary'; label: string }
> = {
  unverified: { variant: 'secondary', label: 'Unverified — tools never ran' },
  ok: { variant: 'success-soft', label: 'Success' },
  'no-tools': { variant: 'secondary', label: 'No tools run' },
  error: { variant: 'destructive-soft', label: 'Error' },
};

export function delegateOutcomeState(tel: DelegateTelemetryView): DelegateOutcomeState {
  // Checked first: an ungrounded run is reported as "ok" upstream, and calling
  // that a success is the thing FRONT-1169 exists to stop.
  if (tel.toolsUnreachable) return 'unverified';
  if (tel.outcome === 'ok') return 'ok';
  if (tel.outcome === 'no-tools-executed') return 'no-tools';
  return 'error';
}

export function DelegateStats({ tel, showBlocks }: { tel: DelegateTelemetryView; showBlocks: boolean }) {
  const { variant: outcomeVariant, label: outcomeLabel } =
    OUTCOME_PRESENTATION[delegateOutcomeState(tel)];
  const inTok = tel.inputTokens ?? 0;
  const outTok = tel.outputTokens ?? 0;
  const totalTok = tel.totalTokens ?? inTok + outTok;
  const inPct = totalTok > 0 ? Math.round((inTok / totalTok) * 100) : 0;
  const blocks = showBlocks && totalTok > 0 ? delegateBlocks(tel) : null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5 text-[length:var(--text-2xs)]">
        <Badge variant={outcomeVariant}>{outcomeLabel}</Badge>
        {/* FRONT-2670: compute leads the stats; the token bar below stays. */}
        {blocks && <StatChip value={blocks.value} title={blocks.hint} testId="delegate-blocks-chip" />}
        <StatChip value={String(tel.toolsRun)} label={tel.toolsRun === 1 ? 'tool' : 'tools'} />
        {!showBlocks && totalTok > 0 && (
          <StatChip value={formatTokenCount(totalTok)} label="tokens" title={`${inTok} in · ${outTok} out`} />
        )}
        {tel.totalMs != null && <StatChip value={formatMs(tel.totalMs)} />}
        {tel.upstreamCalls != null && tel.upstreamCalls > 1 && (
          <StatChip value={String(tel.upstreamCalls)} label="calls" />
        )}
        {/* FRONT-1063: these rounds are Eddy approving the specialist's tool
            calls on the user's behalf, without asking. Labelled "approvals" it
            read as "you approved N things" — the opposite of what happened.
            Says auto-approved, and the tooltip spells out who decided. */}
        {tel.approvalRounds != null && tel.approvalRounds > 0 && (
          <StatChip
            value={String(tel.approvalRounds)}
            label="auto-approved"
            title={`EDDY approved ${tel.approvalRounds} tool ${
              tel.approvalRounds === 1 ? 'call' : 'calls'
            } for this agent without asking you`}
          />
        )}
        {tel.repromptTurns != null && tel.repromptTurns > 0 && (
          <StatChip value={String(tel.repromptTurns)} label="reprompts" />
        )}
        {tel.responseChars != null && <StatChip value={formatTokenCount(tel.responseChars)} label="chars" />}
      </div>
      {totalTok > 0 && (inTok > 0 || outTok > 0) && (
        <div className="space-y-1">
          <Tip label={`${inTok} input · ${outTok} output tokens`}>
            <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-border">
              <div className="bg-primary/70" style={{ width: `${inPct}%` }} />
              <div className="bg-primary/30" style={{ width: `${100 - inPct}%` }} />
            </div>
          </Tip>
          <div className="flex justify-between text-[length:var(--text-2xs)] text-muted-foreground">
            <span>{formatTokenCount(inTok)} in</span>
            <span>{formatTokenCount(outTok)} out</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * FRONT-1100: the single source of truth for "did this tool result carry an
 * error", shared by the status dot and the summary label so they can never
 * disagree. Only a TOP-LEVEL `error` key with a truthy value counts — a
 * nested `{"nested":{"error":"x"}}` or a benign `{"error":0|null|false}`
 * value is not an error. A plain-text (non-JSON) result is never red from
 * content alone. String output gets exactly ONE parse layer — a
 * double-encoded payload is treated as opaque text, matching what the
 * summary label can actually display.
 */
export function hasTopLevelError(output: unknown): boolean {
  if (output == null) return false;
  let candidate: unknown = output;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return false;
    }
  }
  if (typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate)) {
    return Boolean((candidate as Record<string, unknown>).error);
  }
  return false;
}

/**
 * FRONT-1130 (case 4): a memory-tool result can run cleanly — no error,
 * nothing thrown — and still have changed nothing (a refused write, an
 * all-failed/all-unchanged batch, a merge that found no duplicates, an
 * update with no facts to apply). `hasTopLevelError` alone can't tell
 * those apart from a real success, so the step rendered a green "Done"
 * dot for a save that never happened (the FRONT-1126 case, seen here on
 * the chat-step surface rather than the notifications one). Reads the
 * SAME shared `changed: false` signal the backend's
 * `resultDidNotChangeAnything` (memory-write.ts) already opts every no-op
 * memory result into, so the two surfaces can't disagree. Same one-parse
 * rule as `hasTopLevelError`.
 */
export function hasNoChange(output: unknown): boolean {
  if (output == null) return false;
  let candidate: unknown = output;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return false;
    }
  }
  if (typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate)) {
    return (candidate as Record<string, unknown>).changed === false;
  }
  return false;
}

/**
 * FRONT-804: `propose_plan`'s existing already-active-plan guard returns
 * `{ error, activePlan }` instead of the normal `{ status, plan }` shape when
 * the model redundantly re-calls it. `ProposePlanCard`'s parser only
 * understands the success shape, so routing the gate's error output into it
 * rendered a blank, title-less "Pending" chip duplicating the real,
 * already-active plan shown elsewhere.
 *
 * FRONT-1412 review finding: moved to `plan-status.ts` (dependency-free) so
 * the phone surface can import this exact check without pulling in this
 * whole module (cytoscape and friends) — re-exported here so this file's own
 * call site above and `propose-plan-shape.test.tsx` are untouched.
 */
export { isProposePlanSuccessShape } from './plan-status';

/** FRONT-3263: the chip row learns of a "moved to job" card's job from the card itself, not only from its start event. */
function BranchedJobWatch({ taskId }: { taskId: string }) {
  useEnsureChatTaskListed(taskId);
  return null;
}

/**
 * FRONT-855 (Task 2): resolve a `delegate_to_agent` call's `agentId` to a
 * human-readable specialist name. Mirrors the lookup already used by the
 * agent-performance panel (FRONT-320) — the built-in roster is keyed by id,
 * custom agents are a flat list; either source failing just means we fall
 * back to the id (never a hard error).
 */
function useDelegateAgentName(agentId: string | undefined): string | undefined {
  const { data: roster } = useAgentRoster();
  const { data: customAgents } = useCustomAgents();
  if (!agentId) return undefined;
  return roster?.[agentId]?.name ?? customAgents?.find((a) => a.id === agentId)?.name;
}

/** The plain download-chip rendering `ToolCallCard`'s outputFiles strip has
 *  always used for a non-image produced file — extracted so `OutputFileImage`
 *  below can reuse it verbatim as its onError fallback rather than
 *  duplicating the markup. */
function OutputFileDownloadChip({
  filename,
  url,
  sizeBytes,
}: {
  filename: string;
  url: string;
  sizeBytes: number;
}) {
  return (
    <a href={url} download={filename} className="inline-flex items-center gap-1.5 text-foreground hover:underline">
      <DownloadSimpleIcon size={12} className="shrink-0 text-muted-foreground" />
      <span className="truncate max-w-[280px]">{filename}</span>
      <span className="text-muted-foreground">({formatOutputFileSize(sizeBytes)})</span>
    </a>
  );
}

/**
 * FRONT-2594: the download chip. When the entry is a Markdown file the Canvas
 * holds as an item, FRONT-2744 turns the NAME into the Canvas opener (plain
 * click; Ctrl/Cmd/Shift/middle-click keep the Files-page href) and moves the
 * download to its own icon. Without a Canvas context (flag off, other
 * surfaces) or when the item is not in the list it returns the chip ALONE,
 * so those surfaces stay byte-identical.
 */
function OutputFileChipRow({ entry }: { entry: CustomToolOutputFile & { url: string } }) {
  const canvas = useCanvas();
  const chip = <OutputFileDownloadChip filename={entry.filename} url={entry.url} sizeBytes={entry.sizeBytes} />;
  const itemKey = entry.fileId && isMarkdownOutputFile(entry) ? `file:${entry.fileId}` : null;
  const openInCanvas = useCanvasOpenHandler(itemKey ?? '');
  if (!itemKey || !canvas?.items.some((i) => i.key === itemKey)) return chip;
  // FRONT-2744: with the Canvas available the NAME opens the file beside the
  // chat (real href to the Files page, so a modifier click opens a new tab) and
  // the download moves to its own icon, as on `OutputFileImage`.
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <a
        href={canvasNewTabHref({ kind: 'file', id: entry.fileId! })}
        target="_blank"
        rel="noreferrer noopener"
        onClick={openInCanvas}
        data-testid="output-file-name"
        className="inline-flex items-center gap-1.5 text-foreground hover:underline"
      >
        <span className="truncate max-w-[280px]">{entry.filename}</span>
      </a>
      <span className="text-muted-foreground">({formatOutputFileSize(entry.sizeBytes)})</span>
      <Tip label={`Download ${entry.filename}`}>
        <a
          href={entry.url}
          download={entry.filename}
          aria-label={`Download ${entry.filename}`}
          data-testid="output-file-download"
          className="text-muted-foreground hover:text-foreground"
        >
          <DownloadSimpleIcon size={12} />
        </a>
      </Tip>
      <CanvasOpenButton itemKey={itemKey} />
    </span>
  );
}

/**
 * FRONT-2035: a produced-file thumbnail for an entry `isImageOutputFile`
 * accepted. Clicking the picture itself opens the full image in a new tab —
 * same-origin, cookie-authed `/api/files/<id>/raw` — rather than a lightbox,
 * since this is a link to a file that already exists rather than a
 * model-authored generation (contrast `GeneratedImageCard`'s dialog). If the
 * `<img>` itself fails to decode (e.g. a content type the raw route's own
 * narrower allow-list won't actually serve inline, such as `image/svg+xml`),
 * it falls back to the plain download chip rather than showing a
 * broken-image icon — this cannot be driven from a component test (SSR-only,
 * no real `<img>` decode to fail `onError` on). FRONT-2035 review round 2:
 * this IS exercised by a browser spec — the failure-path case in
 * `tests/e2e/specs/chat/chat-inline-file-image.spec.ts` (E2E-CHAT-29) routes
 * the raw file path to a 404 and asserts the thumbnail disappears and the
 * plain download chip renders in its place. Its phone counterpart
 * (`PwaOutputFileImage`, `pwa-message.tsx`) shares this exact `onError`
 * shape but has no browser spec of its own proving it — see that
 * component's docblock.
 *
 * FRONT-2035 review round 1, finding #7: the view-in-new-tab link alone lost
 * the one-click "save this file" affordance the plain download chip
 * (`OutputFileDownloadChip`) still has — a `target="_blank"` navigation is
 * not a download. A dedicated download icon sits beside the caption so the
 * thumbnail keeps that affordance too; it's a SIBLING of the image link, not
 * nested inside it, since an `<a>` inside another `<a>` is invalid markup.
 */
function OutputFileImage({ entry }: { entry: CustomToolOutputFile & { url: string } }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return <OutputFileDownloadChip filename={entry.filename} url={entry.url} sizeBytes={entry.sizeBytes} />;
  }
  return (
    <div className="inline-flex flex-col items-start gap-1">
      <a href={entry.url} target="_blank" rel="noopener noreferrer">
        <img
          src={entry.url}
          alt={entry.filename}
          data-testid="output-file-image"
          className="max-w-[220px] max-h-[160px] rounded-md border border-border object-contain bg-muted/40"
          onError={() => setBroken(true)}
        />
      </a>
      <span className="inline-flex items-center gap-1.5 text-foreground">
        <span className="truncate max-w-[280px]">{entry.filename}</span>
        <span className="text-muted-foreground">({formatOutputFileSize(entry.sizeBytes)})</span>
        <Tip label={`Download ${entry.filename}`}>
          <a
            href={entry.url}
            download={entry.filename}
            aria-label={`Download ${entry.filename}`}
            data-testid="output-file-image-download"
            className="text-muted-foreground hover:text-foreground"
          >
            <DownloadSimpleIcon size={12} />
          </a>
        </Tip>
      </span>
    </div>
  );
}

export function ToolCallCard({
  name,
  input,
  output,
  state,
  errorText,
  delegationProgress,
}: {
  name: string;
  input?: unknown;
  output?: unknown;
  state: string;
  /** F9 (PR review): the message for an `output-error` part — that state
   *  carries its text here, never on `output` (see `getToolInfo`'s own
   *  comment). Optional so every other existing caller/shape is unaffected. */
  errorText?: string;
  /** A running delegation's live steps and specialist text. */
  delegationProgress?: DelegationProgressView | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const isRunning = state === 'input-streaming' || state === 'input-available';
  // FRONT-3263: was this card still running when it appeared? Only then did this page see a hand-off happen live.
  const [mountedRunning] = useState(isRunning);
  const elapsedMs = useElapsed(isRunning);
  const isDelegate = name === 'delegate_to_agent';
  const agentId =
    isDelegate && input && typeof input === 'object' && !Array.isArray(input)
      ? (input as Record<string, unknown>).agentId
      : undefined;
  const resolvedAgentName = useDelegateAgentName(typeof agentId === 'string' ? agentId : undefined);
  const showBlocks = useShowComputeBlocks();
  const resultStr = output != null ? (typeof output === 'string' ? output : JSON.stringify(output, null, 2)) : undefined;
  // FRONT-1644: a custom tool's result can carry produced files (detected by
  // diffing the sandbox workspace, persisted to Files) — every custom tool
  // falls through to this generic card since its name is dynamic and can
  // never match the specialized-card allowlist above.
  const outputFiles = getCustomToolOutputFiles(output);
  // FRONT-717: delegate_to_agent results carry telemetry — render a snackable
  // stats row instead of the raw JSON blob.
  const delegateTel = name === 'delegate_to_agent' ? parseDelegateTelemetry(output) : null;
  // FRONT-2320: an inline run_skill result hands the MODEL the skill's own
  // instructions to carry out itself, in this same turn — the tool calls
  // after it ARE the skill running. The collapsed summary names the skill
  // ("Skill: <name>") instead of a slice of the brief's own JSON. Not
  // "Running …": the card stays in history long after the skill finished.
  const inlineSkillName =
    name === 'run_skill' &&
    output != null &&
    typeof output === 'object' &&
    !Array.isArray(output) &&
    (output as Record<string, unknown>).inline === true &&
    typeof (output as Record<string, unknown>).skill === 'string'
      ? ((output as Record<string, unknown>).skill as string)
      : undefined;
  const isError = state === 'output-error' || (delegateTel ? delegateTel.outcome === 'error' : hasTopLevelError(output));
  // FRONT-1130 (case 4): a clean, non-error result that changed nothing
  // (a refused write, an all-failed batch, a merge with zero duplicates, …)
  // is neither a failure nor a real "Done" — render it as a distinct
  // neutral state rather than the green success dot.
  // FRONT-1169: a delegation whose forwarded tools never ran belongs in this
  // same neutral state. It is not an error (upstream answered, and an empty
  // read can be legitimate) but it is emphatically not the green "Done" —
  // nothing was retrieved. Reusing the FRONT-1130 state rather than adding a
  // fourth one keeps one vocabulary for "finished, but nothing happened".
  const isNoChange =
    !isError && !isRunning && (hasNoChange(output) || delegateTel?.toolsUnreachable === true);
  // FRONT-855 (Task 2): while running, replace the static "Running..." text
  // with a live elapsed timer; delegate calls additionally show the
  // resolved specialist name + "working" instead of the generic tool label.
  const runningSummary = isRunning
    ? isDelegate
      ? `${resolvedAgentName ?? humanizeToolName(name)} · working · ${formatElapsed(elapsedMs)}`
      : `working · ${formatElapsed(elapsedMs)}`
    : undefined;
  // FRONT-3211: a slow delegation that left the turn as a chat task. Its
  // result is the hand-off note, not the agent's answer, so the card says
  // where the work went instead of a green "Done" (or a stuck "working").
  const branched = isDelegate && !isRunning ? readBranchedDelegation(output) : null;
  const branchedText = branched ? branchedSummary(branched) : undefined;
  const watchedId = watchedJobId(branched, mountedRunning);
  const otherSummary = runningSummary
    ? runningSummary
    : inlineSkillName
      ? `Skill: ${inlineSkillName}`
      : delegateTel
      ? [
          `${delegateTel.toolsRun} ${delegateTel.toolsRun === 1 ? 'tool' : 'tools'}`,
          // FRONT-1169: an "ok" run whose tools never actually ran is the one
          // case where the green dot and the confident answer are both
          // misleading. Say so on the row, next to the tool count it
          // contradicts.
          delegateTel.toolsUnreachable ? 'tools unreachable' : null,
          // FRONT-1918: say on the collapsed row that the agent got the
          // user's files — and, louder, when one did not reach it.
          filesSentLabel(delegateTel.filesSent),
          filesNotSentLabel(delegateTel.filesNotSent),
          // FRONT-1063: the auto-approve count belongs on the COLLAPSED row.
          // It was only rendered inside the accordion, which is shut by
          // default — so "silently auto-approved" was literal: the user had to
          // expand a tool card to learn Eddy had approved writes on their
          // behalf. Disclosure the user never opens is not disclosure.
          delegateTel.approvalRounds != null && delegateTel.approvalRounds > 0
            ? `${delegateTel.approvalRounds} auto-approved`
            : null,
          collapsedUsageText(delegateTel, showBlocks),
          delegateTel.totalMs != null ? formatMs(delegateTel.totalMs) : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : resultStr
        ? (() => {
            try {
              const parsed = typeof output === 'string' ? JSON.parse(output) : output;
              const p = parsed as Record<string, unknown>;
              return hasTopLevelError(output)
                ? String(p.error).slice(0, 60)
                : hasNoChange(output)
                  ? String(p.reason ?? p.message ?? 'No change').slice(0, 60)
                  : String(p.message ?? p.status ?? 'Done').slice(0, 60);
            } catch {
              return resultStr.split('\n')[0].slice(0, 60);
            }
          })()
        // F9 (PR review): a call that ended in `output-error` never carries
        // an `output` — its message rides `errorText` instead — so without
        // this branch a finished, failed call fell all the way through to
        // the same "Running..." text a genuinely still-pending call shows,
        // even though `isRunning` is already false and no timer is ticking.
        : errorText
          ? errorText.slice(0, 60)
          : 'Running...';
  const summary = runningSummary ?? branchedText ?? otherSummary;

  const displayName = humanizeToolName(name);
  const showRawId = displayName !== name;
  return (
    <Accordion
      type="single"
      collapsible
      value={expanded ? 'tool' : ''}
      onValueChange={(v) => setExpanded(v === 'tool')}
      className="border border-border rounded-lg overflow-hidden"
      data-testid="tool-call-card"
      data-tool-name={name}
    >
      {watchedId && <BranchedJobWatch taskId={watchedId} />}
      <AccordionItem value="tool" className="border-b-0">
        <AccordionTrigger
          className={cn(
            'items-center gap-2 px-3 py-2 text-xs hover:brightness-110 hover:no-underline [&>svg]:text-muted-foreground',
            expanded && 'bg-muted',
          )}
        >
          <span
            className={`size-2 rounded-full shrink-0 ${branched ? 'bg-primary/50' : statusDotClass({ isError, isRunning, isNoChange })}`}
            data-branched={branched ? 'true' : undefined}
          />
          <Tip label={showRawId ? name : undefined}>
            <span className="font-medium text-foreground">
              {displayName}
            </span>
          </Tip>
          {showRawId && expanded && (
            <span className="font-mono text-[length:var(--text-2xs)] text-muted-foreground/70">{name}</span>
          )}
          <span className="ml-auto mr-1 text-muted-foreground truncate max-w-[50%]">{summary}</span>
        </AccordionTrigger>
        {isRunning && isDelegate && delegationProgress && (
          // A sibling of the trigger, like the files strip below: closed by default, the accordion body is never seen.
          <DelegationProgressStrip progress={delegationProgress} expanded={expanded} agentName={resolvedAgentName} />
        )}
        {outputFiles.length > 0 && (
          // FRONT-1644 review finding: this MUST be a sibling of the trigger,
          // not inside AccordionContent — a produced file is disclosure the
          // user needs without opening the audit row (same principle as the
          // FRONT-1063 auto-approve-count fix on `summary` above: "disclosure
          // the user never opens is not disclosure"). Also can't live inside
          // AccordionTrigger itself — that's a button, and a nested <a> is
          // invalid markup that would fight the toggle click.
          // `expanded && 'bg-muted'` mirrors the trigger's own highlight so
          // the strip reads as one continuous block with it when open. The
          // strip's `border-t` is the ONLY seam between it and the trigger
          // above; AccordionContent below (see its className) drops its own
          // top border whenever this strip is present, so the two same-
          // colored regions never stack a second, redundant line between
          // them (review finding #11 — a hairline between two identically
          // `bg-muted` blocks reads as a doubled rule through one block).
          <div
            data-testid="custom-tool-output-files"
            className={cn(
              'px-3 py-1.5 border-t border-border flex flex-col gap-1 items-start text-xs',
              expanded && 'bg-muted',
            )}
          >
            {/* Review finding #10: only claim the save happened when at
                least one entry actually has a url — a run where every file
                was skipped must not say "saved". */}
            {hasSavedOutputFile(outputFiles) && (
              <span className="text-muted-foreground/70">Saved to your Files:</span>
            )}
            {outputFiles.map((f, i) => {
              if (isImageOutputFile(f)) return <OutputFileImage key={i} entry={f} />;
              if (f.url) {
                return <OutputFileChipRow key={i} entry={{ ...f, url: f.url }} />;
              }
              return (
                <span key={i} className="text-muted-foreground/70 italic">
                  {f.filename} — {f.skipped}
                </span>
              );
            })}
          </div>
        )}
        <AccordionContent
          className={cn(
            'px-3 py-2 text-xs bg-muted space-y-2',
            // Review finding #11: only draw this border when there's no
            // outputFiles strip directly above supplying the one seam this
            // region needs — with the strip present, Content continues the
            // SAME bg-muted block the strip started, and a second border
            // there would read as a doubled line through one region.
            outputFiles.length === 0 && 'border-t border-border',
          )}
        >
          {input != null && (
            <div>
              <span className="text-muted-foreground font-medium">Input:</span>
              {/* FRONT-2266: capped so a very long raw tool argument (e.g. a
                  20,000-char desktop-task goal) can't push the rest of the
                  message far off screen — the phone equivalent is already
                  capped at 240px (pwa.css's .p-tool-body). */}
              <pre className="bg-background p-2 rounded text-muted-foreground overflow-x-auto whitespace-pre-wrap mt-1 max-h-60 overflow-y-auto">
                {/* FRONT-2716: secrets in the arguments (an MCP server's
                    `env`, any MCP_HEADER_* entry) are masked here, the same
                    way the tool's own output already masks them. */}
                {toolInputText(name, input)}
              </pre>
            </div>
          )}
          {delegateTel ? (
            <div>
              <span className="text-muted-foreground font-medium">Stats:</span>
              <div className="mt-1">
                <DelegateStats tel={delegateTel} showBlocks={showBlocks} />
              </div>
              {resultStr && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-muted-foreground">Raw output</summary>
                  <pre className="bg-background p-2 rounded text-muted-foreground overflow-x-auto whitespace-pre-wrap mt-1">{resultStr}</pre>
                </details>
              )}
            </div>
          ) : resultStr ? (
            <div>
              <span className="text-muted-foreground font-medium">Output:</span>
              <pre className="bg-background p-2 rounded text-muted-foreground overflow-x-auto whitespace-pre-wrap mt-1 max-h-60 overflow-y-auto">{resultStr}</pre>
            </div>
          ) : (
            // R1b (PR review, FRONT-2275): an `output-error` part has no
            // `output` at all — `resultStr` is undefined — so the expanded
            // panel used to show only the Input block and nothing explaining
            // the failure. Same source `errorText` the collapsed summary
            // above already reads (see the F9 comment on `resultStr`'s
            // ternary), just not truncated to 60 chars here.
            errorText && (
              <div>
                <span className="text-muted-foreground font-medium">Error:</span>
                <pre className="bg-background p-2 rounded text-destructive overflow-x-auto whitespace-pre-wrap mt-1">{errorText}</pre>
              </div>
            )
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function isErrorMessage(content: string): boolean {
  if (!content) return false;
  const lower = content.toLowerCase();
  return (
    lower.startsWith('i encountered an error') ||
    lower.includes('error while processing') ||
    /\berror:\s*\d{3}\b/.test(lower) ||
    /\b(500|502|503)\s*\{/.test(content)
  );
}

// Shared copy-to-clipboard-with-feedback behavior (matches the
// Copy/Check pattern already used in code-editor.tsx) so both the user
// and assistant message toolbars confirm a successful copy the same way.
function useCopyFeedback() {
  const [copied, setCopied] = useState(false);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — clipboard unavailable');
    }
  };
  return { copied, copy };
}

function UserMessageToolbar({
  messageId,
  onRetry,
  onDelete,
  text,
}: {
  messageId: string | undefined;
  onRetry?: (input: { id?: string | number; content?: string }) => void | Promise<void>;
  onDelete?: (input: { id?: string | number; role?: string; content?: string }) => void | Promise<void>;
  text: string;
}) {
  const { copied, copy } = useCopyFeedback();
  return (
    <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-0.5 -mt-0.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
            // Always enabled when a handler is wired. The hook resolves
            // the matching DB row via /history when the AI SDK kept a
            // synthetic id past our post-stream setMessages reload.
            disabled={!onRetry}
            onClick={() => onRetry?.({ id: messageId, content: text })}
            aria-label="Branch this message — sends the same prompt on a new branch"
          >
            <GitFork size={13} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Branch — fork a new conversation from here</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => void copy(text)}
            aria-label={copied ? 'Copied' : 'Copy message text'}
          >
            {copied ? <Check size={13} weight="bold" /> : <Copy size={13} />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{copied ? 'Copied' : 'Copy'}</TooltipContent>
      </Tooltip>
      {onDelete && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => onDelete({ id: messageId, role: 'user', content: text })}
              aria-label="Delete this message"
            >
              <Trash size={13} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Delete</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * The model chip's anchor, or an inert span carrying the same classes when the
 * shell it renders in has nowhere to navigate to (FRONT-1014).
 *
 * A component rather than a ternary at the call site: the chip's children are
 * a dozen lines of drift-highlighting markup, and duplicating them across two
 * branches is how the two copies come to disagree. `EddyInfluenceDot` above
 * makes the same swap inline because its child is a single glyph.
 *
 * `forwardRef` plus a spread `...rest`, not a plain function — its only call
 * site (below) is the sole child of `<TooltipTrigger asChild>`, which clones
 * its child via Radix's `Slot` to inject the ref and the pointer/focus
 * handlers that open the tooltip. A component that doesn't forward those
 * silently drops them: the tooltip never opened on ANY message's model chip
 * (code review, PR #1345) from the refactor that extracted this component out
 * of an inline `<Link>` (which forwarded them by virtue of being one).
 */
const ModelChipLink = forwardRef<
  HTMLAnchorElement | HTMLSpanElement,
  ComponentPropsWithoutRef<'span'> & { linkDisabled?: boolean }
>(function ModelChipLink({ linkDisabled, children, className, ...rest }, ref) {
  const chipClassName = cn(
    'transition-colors flex items-center gap-1.5',
    linkDisabled ? 'cursor-default' : 'hover:text-foreground hover:underline',
    className,
  );
  if (linkDisabled) {
    return (
      <span ref={ref as React.Ref<HTMLSpanElement>} className={chipClassName} {...rest}>
        {children}
      </span>
    );
  }
  return (
    <Link
      ref={ref as React.Ref<HTMLAnchorElement>}
      to={settingsPath('models')}
      className={chipClassName}
      {...rest}
    >
      {children}
    </Link>
  );
});

function MessageFooter({
  message,
  sessionId,
  isStreaming,
  onDelete,
  onSaveAsDocument,
  linkDisabled,
}: {
  message: UIMessage;
  /** FRONT-2387: half of the key `AudioAnswerButton` reads/writes playback
   *  state under — see that component's own prop docblock. */
  sessionId: string;
  /** FRONT-2387: this message is still streaming — `AudioAnswerButton` shows
   *  no control at all until it finishes. Fed `MessageBubbleProps`'
   *  `audioAnswerStreaming` (fix round 4, #8), not its narrower `isStreaming`
   *  — see that prop's docblock for why the two differ. */
  isStreaming?: boolean;
  onDelete?: (input: { id?: string | number; role?: string; content?: string }) => void | Promise<void>;
  onSaveAsDocument?: (text: string) => void;
  /**
   * Same gate the Eddy Stone gem above already honours, for the same reason
   * (FRONT-1014): the day-one chat runs inside the pre-app shell, which has no
   * settings route to land on, so following this link there yanks the user
   * clean out of onboarding. The gem was gated and this link was not — every
   * real assistant reply carries model metadata, so the ungated one was the
   * likelier click of the two. Found by a review panel while this ticket was
   * retargeting the link's destination.
   */
  linkDisabled?: boolean;
}) {
  const hide = useHiddenMessages((s) => s.hide);
  const meta = readMessageMeta(message);
  // Empty when the chip is inert, so no branch below can promise a click that
  // does nothing. Leading space is inside the string: it follows a full stop.
  const modelChipInvitation = linkDisabled ? '' : ' Click to change the model for this role.';
  // Flatten the assistant text so the delete-by-content fallback in
  // the hook can match the persisted row when the id is synthetic.
  const assistantText = message.parts
    .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
    .map((p) => p.text)
    .join('');
  // Generated artifacts (image, app) so "Save as document" keeps them, not just
  // the prose — and so an image-only reply is still saveable.
  const artifacts = messageArtifactsMarkdown(message);
  // FRONT-2601: a reply whose boilerplate sentence is hidden behind a
  // text-replacing continue offer copies/saves nothing from that text — the
  // person never saw it. (`assistantText` above stays the real persisted text:
  // delete-by-content matches on it.)
  const visibleText = hasTextReplacingContinueOffer(message.parts) ? '' : assistantText;
  const saveBody = [visibleText, artifacts].filter((s) => s.trim()).join('\n\n');
  const hasUsage = meta.usage?.inputTokens != null || meta.usage?.outputTokens != null;
  const showBlocks = useShowComputeBlocks();
  const blocksLabel = showBlocks && hasUsage ? footerBlocksLabel(meta) : null;
  const { copied, copy } = useCopyFeedback();
  return (
    <div className="flex items-center gap-1.5 px-1">
      {saveBody.trim() && (
        <Button
          type="button"
          onClick={() => void copy(saveBody)}
          variant="ghost"
          size="icon-xs"
          tooltip={copied ? 'Copied' : 'Copy this response'}
          aria-label={copied ? 'Copied' : 'Copy this response'}
          className="opacity-35 hover:opacity-100 text-muted-foreground"
        >
          {copied ? <Check size={13} weight="regular" /> : <Copy size={13} weight="regular" />}
        </Button>
      )}
      {onSaveAsDocument && saveBody.trim() && (
        <Button
          type="button"
          onClick={() => onSaveAsDocument(saveBody)}
          variant="ghost"
          size="icon-xs"
          tooltip="Save this response as a new document"
          aria-label="Save this response as a new document"
          className="opacity-35 hover:opacity-100 text-muted-foreground"
        >
          <FilePlus size={13} weight="regular" />
        </Button>
      )}
      {message.id && (
        <AudioAnswerButton
          sessionId={sessionId}
          renderKey={renderKeyOf(message) ?? String(message.id)}
          text={finalAnswerText(message.parts)}
          isStreaming={isStreaming}
        />
      )}
      {message.id && (
        <Button
          type="button"
          onClick={() => hide(message.id)}
          variant="ghost"
          size="icon-xs"
          tooltip="Hide this message from view"
          aria-label="Hide this message"
          className="opacity-35 hover:opacity-100 text-muted-foreground"
        >
          <EyeSlash size={13} weight="regular" />
        </Button>
      )}
      {onDelete && (
        <Button
          type="button"
          onClick={() => onDelete({ id: message.id, role: 'assistant', content: assistantText })}
          variant="ghost"
          size="icon-xs"
          tooltip="Delete this message"
          aria-label="Delete this message"
          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
        >
          <Trash size={13} weight="regular" />
        </Button>
      )}
      <div className="flex items-center gap-1.5 text-[length:var(--text-2xs)] leading-none text-muted-foreground/70 font-mono">
        {shouldShowInterruptedChip(meta) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 text-amber-500 dark:text-amber-400">
                <WarningCircle size={11} weight="fill" />
                interrupted
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              This reply was cut short before the model finished. The
              text above is what landed before the stream ended; ask
              again to continue.
            </TooltipContent>
          </Tooltip>
        )}
        {shouldShowInterruptedChip(meta) && (hasUsage || meta.agent || meta.model) && (
          <span className="text-muted-foreground/40">·</span>
        )}
        {blocksLabel ? (
          <Tip label={blocksLabel.hint}>
            <span data-testid="reply-compute-blocks">{blocksLabel.text}</span>
          </Tip>
        ) : (
          hasUsage && (
            <span>
              {formatTokenCount(meta.usage?.inputTokens ?? 0)} in ·{' '}
              {formatTokenCount(meta.usage?.outputTokens ?? 0)} out
            </span>
          )
        )}
        {/*
          Agent + model are clickable when present — they jump straight
          into Settings → Models so the user can change which model
          answers this role next time. Single anchor, single href; keeps
          the footer tidy and prevents click-target shifting when only
          one of the two cells is set.
        */}
        {(meta.agent || meta.model) && (
          <>
            {hasUsage && <span className="text-muted-foreground/40">·</span>}
            <Tooltip>
              <TooltipTrigger asChild>
                <ModelChipLink linkDisabled={linkDisabled} data-testid="message-model-chip">
                  {/*
                    On drift the footer shows BOTH ids so the user can
                    spot at a glance that the proxy didn't honour the
                    requested alias. Amber tint hints "this isn't what
                    you asked for" without being alarmist. No drift →
                    plain single id, neutral colour.
                  */}
                  {meta.requestedModel && meta.model ? (
                    <>
                      <span className="text-amber-600 dark:text-amber-400">
                        {meta.requestedModel}
                      </span>
                      <span className="text-amber-600/60 dark:text-amber-400/60">→</span>
                      <span className="text-amber-600 dark:text-amber-400">{meta.model}</span>
                    </>
                  ) : (
                    meta.model && (
                      <span className="flex items-center gap-1">
                        {/*
                          A verified tick — shown ONLY when the backend
                          confirmed a served id off the response stream
                          (FRONT-136). Without it, the id is the requested
                          alias the proxy never confirmed, so we leave the
                          tick off rather than imply a verification we don't
                          have. Success token keeps the one-green rule.
                        */}
                        {meta.modelConfirmed && (
                          <SealCheckIcon
                            size={12}
                            weight="fill"
                            className="text-success shrink-0"
                            aria-hidden
                          />
                        )}
                        <span>{meta.model}</span>
                      </span>
                    )
                  )}
                  {meta.model && meta.agent && (
                    <span className="text-muted-foreground/40">·</span>
                  )}
                  {meta.agent && <span>{meta.agent}</span>}
                </ModelChipLink>
              </TooltipTrigger>
              <TooltipContent side="top">
                {/* The "click to…" half is conditional, and that is the point.
                    Aikido caught the contradiction: when `linkDisabled` is set
                    the chip renders as an inert span, so a tooltip telling the
                    reader to click it describes something the branch has
                    deliberately made impossible. That state is real — the
                    day-one chat sets it, because its pre-app shell has no
                    settings route to land on.

                    `EddyInfluenceDot` above already does exactly this with its
                    aria-label; the footer just never followed. Same fix, same
                    file, and the invitation is a separate string rather than
                    baked into each of the three branches so a fourth branch
                    cannot reintroduce the mismatch. */}
                {meta.requestedModel ? (
                  <>
                    Proxy routed your request elsewhere — requested{' '}
                    <span className="font-mono">{meta.requestedModel}</span>, served{' '}
                    <span className="font-mono">{meta.model}</span>.{modelChipInvitation}
                  </>
                ) : meta.modelConfirmed && meta.model ? (
                  <>
                    Confirmed: <span className="font-mono">{meta.model}</span> produced this
                    answer (read from the response).{modelChipInvitation}
                  </>
                ) : meta.model ? (
                  <>
                    Requested <span className="font-mono">{meta.model}</span>; the server didn't
                    report which model served it.{modelChipInvitation}
                  </>
                ) : (
                  `Your model for this role${
                    linkDisabled ? '.' : ' — change it in Settings → Models.'
                  }`
                )}
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}

interface MessageBubbleProps {
  message: UIMessage;
  isStreaming?: boolean;
  /**
   * FRONT-2387 fix round 4 (#8): a WIDER streaming gate than `isStreaming`
   * above, for the play-answer control alone (threaded to `MessageFooter`,
   * never to the cursor blink/`ReasoningBlock`/`QuestionsCard` below, which
   * keep reading the narrower `isStreaming`). `isStreaming` is true only for
   * a live LOCAL stream; this is also true while the turn is running on the
   * server with no live stream attached here (a chat surface reopened
   * mid-run), so a partial last answer from before the reload never grows a
   * control it would otherwise get wrongly. Computed by the caller
   * (`chat-messages.tsx`) from the exact same `isTurnActive` value already
   * driving its "…in progress" chip — see that call site for the reasoning.
   */
  audioAnswerStreaming?: boolean;
  /**
   * True when this is the last message in the conversation. Threaded down to
   * `SignalCards` so a `quick-replies` card only shows on the message it was
   * actually seeded on — any later turn means it was answered or overtaken
   * (FRONT-907: a full reload used to re-show already-answered buttons since
   * their "used" state was local-only). Required, not optional: this is the
   * one seam that decides whether a stale quick-reply resurfaces, so the
   * wiring can't be silently dropped (same contract as `sessionId` below).
   */
  isLastMessage: boolean;
  /**
   * True while a button-action user message (FRONT-199) is still being
   * worked on — i.e. it's the latest user message and the turn is active.
   * Drives the action chip's spinner→settle transition. Ignored on normal
   * messages.
   */
  actionRunning?: boolean;
  /**
   * Either an id (preferred — the numeric `chat_messages.id` when
   * available) or the raw user-message text. The hook handles both
   * shapes: numeric id goes straight to the retry endpoint; content
   * triggers a /history lookup to resolve the matching row. The text
   * fallback exists because the AI SDK occasionally hangs onto its
   * synthetic message ids after our post-stream setMessages reload.
   */
  onRetry?: (input: { id?: string | number; content?: string }) => void | Promise<void>;
  /**
   * Delete a persisted message. Accepts either the numeric
   * `chat_messages.id` OR the message role + content — the hook
   * resolves the matching row via /history when the AI SDK held onto
   * its synthetic id past our post-stream setMessages reload (same
   * shape as onRetry, same reason).
   */
  onDelete?: (input: { id?: string | number; role?: string; content?: string }) => void | Promise<void>;
  /**
   * Threaded through so child components (e.g. QuestionsCard) can
   * kick a follow-up turn when the user takes an inline action. The
   * chat-route wires this from `useAgentChat().sendMessage`.
   */
  onSendMessage?: (text: string) => void | Promise<void>;
  /**
   * Text of the user message this assistant turn is replying to (the nearest
   * preceding user message, computed by `chat-messages.tsx` where the
   * ordered array is in scope). Threaded down to `SignalCards` so
   * `ConnectIntegrationCard`'s one-tap Retry can re-send the original
   * request verbatim via `onSendMessage` once the provider is connected
   * (FRONT-847).
   */
  originalUserText?: string;
  /**
   * 2026-07-21 (FRONT-907): starts a live voice call on this chat's own
   * session — threaded down to `SignalCards` for a `quick-replies` option
   * with `action: "call"` (the day-one interview offer's "Let's talk").
   */
  onStartCall?: () => void;
  /**
   * Session id of the chat surface this bubble belongs to. Threaded into
   * session-scoped child cards (QuestionsCard) so their server lookups +
   * mutations hit the right session under the per-surface chat ids
   * (`web:`/`inbox-`), not the global one. Required so the wiring can't be
   * silently dropped — that silent drop is the FRONT-540 regression. (FRONT-540)
   */
  sessionId: string;
  /** Anchor-message-id → ordered sibling branches for the fork navigator. */
  forkGroups?: Map<number, BranchInfo[]>;
  /** Active branch id — used to compute which sibling is "current". */
  activeBranchId?: string;
  /** Switch the active branch when an arrow is clicked. */
  onSwitchBranch?: (branchId: string) => void;
  /** Summarize the currently selected fork into its parent. */
  onMergeBranch?: (branchId: string) => void | Promise<void>;
  /** Branch id currently being merged (drives the spinner). */
  mergingBranchId?: string | null;
  /**
   * When provided, assistant messages show a "Save as document" action that
   * saves that single response as a new document. Wired by the inbox chat
   * modal; omitted in the main chat (so the action only appears there).
   */
  onSaveAsDocument?: (text: string) => void;
  /**
   * When true, the Eddy Stone identity-influence badge shows its tooltip
   * but isn't clickable — set by the day-one chat, whose pre-app shell
   * (`AuthLayout`) has no `/settings` route to land on (Marko, 2026-07-29
   * live-test: clicking it there kicked the user clean out of onboarding).
   */
  disableIdentityLink?: boolean;
}

/**
 * Read the optional fork metadata our /history endpoint attaches when a
 * user message starts a new branch. Returns the source message id (the
 * one the user clicked "retry" on) so the bubble can show a back-reference.
 */
function readForkMeta(message: UIMessage): number | null {
  // Prefer metadata.branchedFromMessageId (preserved across the AI SDK's
  // setMessages round-trip); fall back to top-level for back-compat with
  // older payloads.
  const meta = (message as unknown as { metadata?: { branchedFromMessageId?: number } }).metadata;
  if (typeof meta?.branchedFromMessageId === 'number') return meta.branchedFromMessageId;
  const raw = message as unknown as { branchedFromMessageId?: unknown };
  return typeof raw.branchedFromMessageId === 'number' ? raw.branchedFromMessageId : null;
}

// FRONT-1423: the attachment type + reader (`readMessageAttachments`,
// imported above) moved to the dependency-free `image-source.ts` so the
// phone surface can read the same `metadata.attachments` field without
// importing this 1798-line module.

/**
 * Sibling-branch toolbar rendered on a retried user message. Walks the
 * fork group with left/right arrows. Shows `‹ k / N ›` and a tooltip
 * with the active sibling's preview text.
 */
function BranchNav({
  group,
  activeBranchId,
  onSwitch,
  onMerge,
  mergingBranchId,
}: {
  group: BranchInfo[];
  activeBranchId?: string;
  onSwitch: (branchId: string) => void;
  onMerge?: (branchId: string) => void | Promise<void>;
  mergingBranchId?: string | null;
}) {
  const total = group.length;
  if (total < 2) return null;
  const rawIdx = activeBranchId
    ? group.findIndex((b) => b.branchId === activeBranchId)
    : 0;
  const idx = rawIdx >= 0 ? rawIdx : 0;
  const prev = () => onSwitch(group[(idx - 1 + total) % total].branchId);
  const next = () => onSwitch(group[(idx + 1) % total].branchId);
  const active = group[idx];
  const canMerge = !!onMerge && active && active.branchId !== 'main';
  return (
    <div
      className="flex items-center gap-1 text-[length:var(--text-2xs)] text-muted-foreground bg-muted/50 border border-border rounded-full px-1.5 py-0.5"
      role="group"
      aria-label={`Sibling branches: ${idx + 1} of ${total}`}
    >
      <button
        type="button"
        onClick={prev}
        className="size-4 inline-flex items-center justify-center rounded-full hover:bg-muted-foreground/10 hover:text-foreground"
        aria-label="Previous fork"
      >
        <CaretLeft size={10} weight="bold" />
      </button>
      <span className="tabular-nums font-mono text-foreground/80">
        {idx + 1} / {total}
      </span>
      <button
        type="button"
        onClick={next}
        className="size-4 inline-flex items-center justify-center rounded-full hover:bg-muted-foreground/10 hover:text-foreground"
        aria-label="Next fork"
      >
        <CaretRight size={10} weight="bold" />
      </button>
      {canMerge && (
        <Tip label="Merge this branch into its parent">
          <button
            type="button"
            onClick={() => onMerge?.(active.branchId)}
            disabled={mergingBranchId === active.branchId}
            aria-busy={mergingBranchId === active.branchId}
            className="ml-0.5 pl-1.5 border-l border-border/70 inline-flex items-center gap-1 text-foreground/70 hover:text-foreground disabled:opacity-60 disabled:cursor-default"
            aria-label="Merge this branch into its parent"
          >
            {mergingBranchId === active.branchId ? (
              <>
                <CircleNotch size={10} weight="bold" className="animate-spin" />
                <span>Merging…</span>
              </>
            ) : (
              <>
                <GitMergeIcon size={10} weight="bold" />
                <span>Merge</span>
              </>
            )}
          </button>
        </Tip>
      )}
    </div>
  );
}

/**
 * Find the fork group this user message anchors. On a fork branch the
 * cloned user message has `branchedFromMessageId` set — anchor is that
 * source id. On the original branch the message is the anchor itself —
 * its own id is the group key.
 */
function resolveForkGroup(
  message: UIMessage,
  forkGroups: Map<number, BranchInfo[]> | undefined,
): BranchInfo[] | null {
  if (!forkGroups || forkGroups.size === 0) return null;
  const fromMeta = readForkMeta(message);
  if (fromMeta != null) return forkGroups.get(fromMeta) ?? null;
  const ownId =
    typeof message.id === 'string'
      ? parseInt(message.id, 10)
      : (message.id as unknown as number);
  if (Number.isFinite(ownId)) return forkGroups.get(ownId as number) ?? null;
  return null;
}

export function MessageBubble({
  message,
  isStreaming,
  audioAnswerStreaming,
  isLastMessage,
  onRetry,
  onDelete,
  onSendMessage,
  originalUserText,
  onStartCall,
  sessionId,
  forkGroups,
  activeBranchId,
  onSwitchBranch,
  onMergeBranch,
  mergingBranchId,
  onSaveAsDocument,
  actionRunning,
  disableIdentityLink,
}: MessageBubbleProps) {
  const userName = useUserDisplayName();
  const canvas = useCanvas();
  // FRONT-3171: attachments open in the file window beside this chat.
  const windowChrome = useWindowChrome();
  // FRONT-3223: `eddy.ui-refactoring` picks the person's avatar style below.
  const chatWindowV2 = useUiRefactoring();
  // FRONT-773: read the give-up registry directly via useSyncExternalStore,
  // called unconditionally (Rules of Hooks) even though only the
  // assistant-message branch below reads the result — mirrors userName
  // above. This bubble can be rendered under the global
  // ChatProvider OR under the inbox chat modal's own untracked useAgentChat
  // instance (no local ChatProvider there) — useChatContext() would silently
  // resolve to the wrong (global) instance in the modal case, so the
  // module-level registry in resume-retry-policy.ts is read directly instead
  // of going through per-instance React state.
  const gaveUpStreamIds = useSyncExternalStore(subscribeGaveUpStreamIds, getGaveUpSnapshot);

  if (message.role === 'user') {
    const text = message.parts
      .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
      .map((p) => p.text)
      .join('');
    // Read file attachments from metadata — the /history endpoint serialises
    // them there so they survive the UIMessage round-trip unchanged.
    // An uploaded attachment opens in the file window to the right of this
    // chat (eddy, 2026-10-26). A Knowledge document keeps its own page, and
    // outside the window layer (or with a modifier key) the link behaves as before.
    const openAttachmentBeside = (e: React.MouseEvent, a: MessageAttachment) => {
      if (!windowChrome.docked || a.source === 'document' || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      openUploadWindow({ fileId: a.fileId, name: a.name, contentType: a.contentType }, windowChrome.windowId);
    };
    const attachments = readMessageAttachments(message);
    // FRONT-199: button-origin marker. When present we render a friendly
    // status chip in place of the raw instruction the button sent.
    const source = readSourceMeta(message);
    const fromCall = isFromCall(source);
    if (source && !fromCall) {
      const labels = actionChipLabels(source);
      // FRONT-3036: the server-started continuation is "a small grey note" -
      // muted and neutral in both states, never the primary action chip or
      // the green done state. Every other button source keeps its look.
      if (isLateApprovalContinuation(source)) {
        return (
          <div className="group flex flex-col items-end gap-1">
            <div
              data-testid="late-approval-continuation-chip"
              className="flex items-center gap-2 rounded-xl border bg-muted px-3 py-1.5 text-xs text-muted-foreground"
            >
              {actionRunning ? <CircleNotch size={13} className="animate-spin shrink-0" /> : null}
              <span>{actionRunning ? labels.running : labels.done}</span>
            </div>
          </div>
        );
      }
      return (
        <div className="group flex flex-col items-end gap-1">
          <div className="flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-3.5 py-2.5 text-sm font-medium">
            {actionRunning ? (
              <>
                <CircleNotch size={15} className="animate-spin text-primary shrink-0" />
                <span className="text-foreground">{labels.running}</span>
              </>
            ) : (
              <>
                <SealCheckIcon size={15} weight="fill" className="text-success shrink-0" />
                <span className="text-success">{labels.done}</span>
              </>
            )}
          </div>
        </div>
      );
    }
    if (!text && attachments.length === 0) return null;
    const forkedFrom = readForkMeta(message);
    const forkGroup = resolveForkGroup(message, forkGroups);
    const hasNav = forkGroup && forkGroup.length > 1 && !!onSwitchBranch;
    return (
      <div className="group flex flex-col items-end gap-1">
        {hasNav ? (
          <div className="flex items-center gap-1.5">
            <GitFork size={11} weight="bold" className="text-muted-foreground" />
            <BranchNav
              group={forkGroup!}
              activeBranchId={activeBranchId}
              onSwitch={onSwitchBranch!}
              onMerge={onMergeBranch}
              mergingBranchId={mergingBranchId}
            />
          </div>
        ) : forkedFrom != null ? (
          <div className="flex items-center gap-1.5 text-[length:var(--text-2xs)] text-muted-foreground">
            <GitFork size={11} weight="bold" />
            <span>
              Forked from message <span className="font-mono">#{forkedFrom}</span>
            </span>
          </div>
        ) : null}
        <div className="flex items-start justify-end gap-2 w-full">
          <div className="flex flex-col items-end gap-1 max-w-[70%]">
            {fromCall && (
              <span
                className="flex items-center gap-1 text-[length:var(--text-2xs)] text-muted-foreground"
                data-testid="from-the-call-mark"
              >
                <Microphone size={11} aria-hidden /> From the call
              </span>
            )}
            <div className="relative max-w-full min-w-0 bg-primary/10 [--md-table-ground:color-mix(in_oklab,var(--primary)_10%,var(--card))] border border-primary/40 pl-3.5 pr-10 py-2.5 rounded-xl text-sm text-foreground">
              {text && <MarkdownRenderer content={text} />}
              {attachments.length > 0 && (
                <div className={`flex flex-wrap gap-2${text ? ' mt-2' : ''}`}>
                  {attachments.map((a) =>
                    a.kind === 'image' ? (
                      <a
                        key={a.fileId}
                        href={attachmentHref(a)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => openAttachmentBeside(e, a)}
                      >
                        <img
                          src={rawFileUrl(a.fileId)}
                          alt={a.name}
                          // A small thumbnail: a click opens the full picture in
                          // the file window beside the chat (eddy, 2026-10-26).
                          // Off (`eddy.ui-refactoring`), the picture as before.
                          data-testid="message-attachment-thumb"
                          className={
                            chatWindowV2
                              ? 'size-16 rounded-md border border-border object-cover transition-opacity hover:opacity-85'
                              : 'max-h-48 rounded'
                          }
                        />
                      </a>
                    ) : (
                      <a
                        key={a.fileId}
                        href={attachmentHref(a)}
                        onClick={(e) => openAttachmentBeside(e, a)}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-xs hover:bg-muted/80"
                      >
                        {a.name} <span className="uppercase text-[length:var(--text-2xs)] opacity-70">{a.kind}</span>
                      </a>
                    )
                  )}
                </div>
              )}
              {/* The person's own round avatar — the same one the sidebar's user
                  menu shows (eddy, 2026-10-26). With `eddy.ui-refactoring` off,
                  the muted square it was before (FRONT-3223). */}
              {chatWindowV2 ? (
                <InitialsAvatar
                  name={userName}
                  size="sm"
                  tone="primary"
                  data-testid="user-message-avatar"
                  className="absolute top-1.5 right-1.5 rounded-full"
                />
              ) : (
                <InitialsAvatar
                  name={userName}
                  size="sm"
                  tone="muted"
                  data-testid="user-message-avatar"
                  className="absolute top-1.5 right-1.5"
                />
              )}
            </div>
            <UserMessageToolbar
              messageId={message.id}
              onRetry={onRetry}
              onDelete={onDelete}
              text={text}
            />
          </div>
        </div>
      </div>
    );
  }

  if (message.role === 'system') {
    const text = message.parts
      .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
      .map((p) => p.text)
      .join('');
    return (
      <div className="text-center">
        <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">{text}</span>
      </div>
    );
  }

  // FRONT-1728: the user stopped this reply. `/history` sends only what they
  // had seen before pressing Stop; the note under it says the rest is gone.
  const stopped = isStoppedMessage(message);

  const contentSegments = sinkQuestionCardsLast(
    buildOrderedContentSegments(message.parts),
    (seg) => getToolInfo(seg.part).name === ASK_QUESTIONS_TOOL,
  );
  const reasoningParts = message.parts.filter((p): p is Extract<typeof p, { type: 'reasoning' }> => p.type === 'reasoning');
  // Truth Guard (FRONT-276) — a `data-truth-guard-warning` data part means
  // the org has Truth Guard armed and this turn claimed an action or tool
  // call that never actually ran. We surface it as a non-blocking amber
  // caution; the reply itself is still shown.
  const truthGuardReasons = message.parts
    .filter((p) => p.type === 'data-truth-guard-warning')
    .flatMap(
      (p) =>
        (((p as { data?: { reasons?: Array<{ kind: string; message: string; toolName?: string }> } }).data
          ?.reasons) ?? []),
    );
  // FRONT-3154: EDDY posted this message on its own (a chat follow-up). The
  // label is the one place the person learns nobody asked for it.
  const isFollowUp = message.parts.some((p) => p.type === 'data-chat-follow-up');
  // Stall caution (FRONT-770) — a `data-stall-warning` data part means the
  // turn stalled (announced work without doing it, hit the output-token
  // limit, or a hard stream error) after its one bounded corrective retry.
  // ALWAYS rendered, independent of the Truth Guard toggle — "this reply may
  // be incomplete" matters whether or not Truth Guard is armed.
  const stallCautionMessages = message.parts
    .filter((p) => p.type === 'data-stall-warning')
    .map((p) => (p as { data?: { message?: string } }).data?.message)
    .filter((m): m is string => !!m);

  const reasoningText = reasoningParts.map((p) => p.text).join('');
  const hasReasoning = !!reasoningText;
  // Each text segment resolves its own briefing marker / error status
  // independently (FRONT-1685) — this used to run once over the whole
  // message, which only worked because grouping-by-type guaranteed exactly
  // one aggregated text block. `renderSegments` mirrors `contentSegments`
  // 1:1, just with text segments carrying their resolved display data.
  const renderSegments: RenderableSegment[] = contentSegments.map((seg) => {
    if (seg.kind === 'tool') return seg;
    const briefing = extractResearchBriefing(seg.text);
    const displayText = briefing ? briefing.stripped : seg.text;
    return {
      kind: 'text',
      displayText,
      isError: !!displayText && isErrorMessage(displayText),
      briefingRunId: briefing?.runId ?? null,
    };
  });
  const hasTools = renderSegments.some((s) => s.kind === 'tool');
  const hasText = renderSegments.some((s) => s.kind === 'text' && !!s.displayText);
  const lastTextIndex = renderSegments.reduce((lastIndex, segment, index) => {
    const rendersText = segment.kind === 'text' && !!segment.displayText;
    return rendersText ? index : lastIndex;
  }, -1);
  // FRONT-773: this row's reconnect budget was permanently exhausted — show
  // a caution instead of letting the interrupted partial sit there silently.
  const meta = readMessageMeta(message);
  const gaveUpReconnecting = !!meta.streamId && gaveUpStreamIds.has(meta.streamId);
  const reconnectCopy = reconnectBannerCopy(meta.streamFailureReason);

  if (
    !hasText &&
    !hasReasoning &&
    !hasTools &&
    truthGuardReasons.length === 0 &&
    stallCautionMessages.length === 0 &&
    !gaveUpReconnecting &&
    !hasSignalParts(message.parts)
  )
    return stopped ? <StoppedTurnNote /> : null;

  // Specialised renderers for tools that need richer interactions than the
  // generic input/output dump. They recognise the tool by name + by the
  // shape of its output — falls back to ToolCallCard for everything else.
  function renderToolSegment(seg: ToolContentSegment): ReactNode {
    const info = getToolInfo(seg.part);
    const { toolPartIndex } = seg;
    // Tool parts within a single message never reorder, but the raw
    // tool-call id (info.id) can transiently repeat across parts during a
    // stream-resume race, so it is not a safe React key on its own.
    // Combining it with the message's RENDER IDENTITY and the part's position
    // makes the key collision-proof. FRONT-1717 PR 2: this used `message.id`,
    // which the post-stream promotion changes, so every tool card in the
    // just-streamed message was torn down at turn settle — closing the image
    // lightbox (FRONT-835) and resetting the reasoning panel (FRONT-1857).
    // `renderKeyOf` is the same value for that message before and after the
    // promotion.
    const key = `${renderKeyOf(message)}-tool-${toolPartIndex}-${info.id}`;
    // FRONT-2669: the document / sketch / deck tools say one quiet line here —
    // never the generic step, which would print the tool's input (the document body).
    if (isQuietLineTool(info.name)) {
      return <QuietToolLine key={key} tool={info} />;
    }
    if (info.name === 'graph_visualize') {
      const out = info.output as GraphVisualizationOutput | undefined;
      if (out && (out.focal_id || out.error)) {
        return <GraphVisualizationCard key={key} output={out} />;
      }
    }
    if (info.name === 'entity_show' && info.output) {
      return <EntityShowCard key={key} output={info.output as EntityShowOutput} />;
    }
    if (info.name === 'activity_show' && info.output) {
      return <ActivityShowCard key={key} output={info.output as { kind?: string; limit?: number }} />;
    }
    if (info.name === 'schedule_show' && info.output) {
      return <ScheduleShowCard key={key} output={info.output as { kind?: string; limit?: number }} />;
    }
    if (info.name === 'todo_show' && info.output) {
      return <TodoShowCard key={key} output={info.output as Parameters<typeof TodoShowCard>[0]['output']} />;
    }
    if (info.name === 'relationship_path' && info.output) {
      return <RelationshipPathCard key={key} output={info.output as Parameters<typeof RelationshipPathCard>[0]['output']} />;
    }
    if (info.name === 'file_show' && info.output) {
      return <FileShowCard key={key} output={info.output as FileShowOutput} />;
    }
    if (info.name === 'app_publish_html' && info.output) {
      const out = info.output as AppPublishedOutput;
      if (out.app?.slug || out.error) {
        return <AppPublishedCard key={key} output={out} />;
      }
    }
    if (info.name === 'sketch_show' && info.output) {
      const out = info.output as SketchOutput;
      if (out.sketch?.slug || out.error) {
        return <SketchCard key={key} output={out} />;
      }
    }
    if (info.name === 'deck_publish' && info.output) {
      const out = info.output as DeckOutput;
      if (out.deck?.slug || out.error) {
        return <DeckCard key={key} output={out} />;
      }
    }
    // FRONT-2594: a document write gets a card only inside a Canvas context;
    // outside it (flag off, other surfaces) it falls through as before.
    if (info.name === 'file_write' && canvas) {
      const doc = documentWriteOf(info.output);
      if (doc) {
        return (
          <DocumentCard
            key={key}
            filename={doc.filename}
            title={doc.title}
            warnings={doc.warnings}
            attachedTo={doc.attachedTo}
          />
        );
      }
    }
    if (info.name === 'generate_image' && info.output) {
      const out = info.output as GeneratedImageOutput;
      if (out.fileId || out.url || out.error) {
        return <GeneratedImageCard key={key} output={out} />;
      }
    }
    // FRONT-1724: a successful browser_screenshot is a saved file — show it
    // with the page-check card. A failed one stays on the generic card.
    if (info.name === 'browser_screenshot' && info.output) {
      const out = info.output as SiteCheckOutput;
      const hasPicture = Boolean(out.url || out.fileId);
      if (out.ok === true && hasPicture) {
        return <SiteCheckCard key={key} output={out} label="Screenshot" />;
      }
    }
    if (info.name === 'site_check_page' && info.output) {
      const out = info.output as SiteCheckOutput;
      if (isSiteCheckResultShape(out)) {
        return <SiteCheckCard key={key} output={out} />;
      }
    }
    // FRONT-1989: `dispatch_desktop_task` runs the whole operator loop
    // before its tool call returns, so `info.output` only ever carries the
    // FINAL result — DesktopTaskCard reads the live run detail itself
    // (`useOperatorRun`/`useOperatorRunForToolCall`) for everything shown
    // while a task is still in progress. The one thing decided HERE, rather
    // than inside the card, is whether there is a run to show at all: an
    // output with no `runId` at all is a refusal that never reached the run
    // store (unattended session, autonomy tier 0, no owner machine, …) —
    // that falls through to the plain ToolCallCard below like any other
    // tool error, since there is nothing for a desktop-task card to poll.
    if (info.name === 'dispatch_desktop_task') {
      const out = info.output as DesktopTaskToolOutput | undefined;
      if (!out || out.runId) {
        return (
          <DesktopTaskCard
            key={key}
            sessionId={sessionId}
            toolCallId={info.id}
            toolState={info.state}
            input={info.input}
            output={out}
          />
        );
      }
    }
    if (info.name === 'ask_questions') {
      const out = info.output as { status?: string; questions?: { id?: number } } | undefined;
      if (out && (out.status === 'pending' || out.status === 'answered' || out.status === 'cancelled')) {
        return (
          <QuestionsCard
            key={questionsCardKey(key, out.questions?.id)}
            output={out as Parameters<typeof QuestionsCard>[0]['output']}
            sendMessage={onSendMessage}
            sessionId={sessionId}
            turnActive={isStreaming}
          />
        );
      }
    }
    // propose_plan is rendered by ProposePlanCard (FRONT-755): a compact,
    // non-interactive live-status chip. The interactive plan card lives
    // separately in the floating PlanCard above the composer (see
    // routes/home.tsx) — rendering a second interactive card here would
    // duplicate the controls and put them in the wrong place.
    // FRONT-804: only for the tool's SUCCESS shape — the already-active-plan
    // guard's `{ error, activePlan }` output falls through to the generic
    // ToolCallCard below (which already renders top-level errors) instead of
    // a blank duplicate "Pending" chip.
    // /pr-panel finding (2026-08-04): this also means propose_plan now falls
    // through to ToolCallCard for its entire RUNNING phase too (info.output
    // is undefined while streaming, and isProposePlanSuccessShape(undefined)
    // is false) — a second, intentional behavior change beyond the
    // gate-error case. This is a net improvement (it brings propose_plan in
    // line with every other specialized card here — app_publish_html,
    // generate_image, ask_questions — which all already guard on
    // `info.output` truthy before switching away from ToolCallCard), not a
    // regression to "fix" back to eager rendering.
    if (info.name === 'propose_plan' && isProposePlanSuccessShape(info.output)) {
      return <ProposePlanCard key={key} output={info.output} sessionId={sessionId} />;
    }
    // Fall through to the generic ToolCallCard, which gives a small audit
    // entry without the buttons.
    return (
      <ToolCallCard
        key={key}
        name={info.name}
        input={info.input}
        output={info.output}
        state={info.state}
        errorText={info.errorText}
        delegationProgress={info.name === 'delegate_to_agent' ? readDelegationProgress(message.parts, info.id) : null}
      />
    );
  }

  /**
   * FRONT-2744: a plain left click on a link in the reply text that points at a
   * Canvas item opens it beside the chat. Delegated, so MarkdownRenderer's HTML
   * (every link forced to a new tab) is untouched; a modifier click, an
   * external link or any other path falls through to the new tab (E2E-CHAT-09).
   */
  function openCanvasLinkOnClick(e: ReactMouseEvent<HTMLElement>) {
    if (!canvas || !isPlainLeftClick(e)) return;
    const anchor = (e.target as Element | null)?.closest?.('a');
    if (!anchor) return;
    const key = canvasItemKeyForHref(anchor.getAttribute('href'), canvas.items, window.location.origin);
    if (!key) return;
    e.preventDefault();
    canvas.openItem(key);
  }

  function renderTextSegment(
    seg: Extract<RenderableSegment, { kind: 'text' }>,
    idx: number,
  ): ReactNode {
    if (!seg.displayText) return null;
    const isLast = idx === lastTextIndex;
    if (seg.isError) {
      return (
        <div
          key={`text-${idx}`}
          className="relative pl-10 pr-3.5 py-2.5 bg-destructive/10 [--md-table-ground:color-mix(in_oklab,var(--destructive)_10%,var(--card))] border border-destructive/30 rounded-xl max-w-[85%]"
        >
          <EddyAvatar className="absolute top-1.5 left-1.5" />
          <div className="text-sm text-destructive flex items-start gap-2">
            <WarningCircle size={18} weight="fill" className="shrink-0 text-destructive mt-0.5" />
            <div className="flex-1 min-w-0">
              <MarkdownRenderer content={seg.displayText} />
            </div>
          </div>
        </div>
      );
    }
    // `--md-table-ground`: a sideways-scrolling table pins its first column
    // on an opaque fill, which must be the bubble's own (FRONT-2539).
    return (
      <div
        key={`text-${idx}`}
        onClick={canvas ? openCanvasLinkOnClick : undefined}
        className="relative bg-[var(--color-chat-agent)] [--md-table-ground:var(--color-chat-agent)] pl-10 pr-3.5 py-2.5 rounded-xl max-w-[85%] text-sm"
      >
        <EddyAvatar className="absolute top-1.5 left-1.5" />
        <MarkdownRenderer content={seg.displayText} />
        {isLast && isStreaming && <span className="inline-block w-1.5 h-4 ml-0.5 bg-foreground/50 animate-pulse" />}
        {/* Gated on the segment carrying the briefing marker, NOT on being the
            last segment. Since FRONT-1685 renders parts in document order, a
            briefing followed by a tool call and more prose is no longer the
            final text segment — pairing Sources with `isLast` would silently
            drop it in exactly that case. The streaming cursor and the Eddy dot
            genuinely do belong on the last segment. */}
        {seg.briefingRunId != null && <SourcesBlock runId={seg.briefingRunId} />}
        {isLast &&
          (() => {
            const eddy = readEddyMeta(message);
            return eddy.used ? (
              <EddyInfluenceDot sections={eddy.sections} linkDisabled={disableIdentityLink} />
            ) : null;
          })()}
      </div>
    );
  }

  return (
    <div className="group flex items-start gap-2">
      {/* FRONT-3223 (eddy, 2026-10-28): clearer spacing between a reply's blocks. */}
      <div data-testid="assistant-blocks" className={chatWindowV2 ? 'flex flex-col gap-2.5 min-w-0 flex-1' : 'flex flex-col gap-1 min-w-0 flex-1'}>
        {isFollowUp && (
          <span
            data-testid="chat-follow-up-label"
            className="inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-md bg-[var(--eddy-blue-soft)] py-0.5 pl-1.5 pr-2 text-xs font-medium text-[var(--eddy-blue-text)]"
          >
            <ArrowClockwise aria-hidden="true" className="size-3" weight="bold" />
            EDDY followed up
          </span>
        )}
        {hasReasoning && <ReasoningBlock text={reasoningText} isStreaming={isStreaming} />}
        {(() => {
          // Walks `renderSegments` in order (the FRONT-1685 ordering fix): a
          // tool card renders exactly where the model emitted it, with
          // consecutive tool segments still grouped into one wrapper (same
          // spacing as before) and text segments rendered as their own
          // bubbles around them, in place, rather than every tool card
          // rendering before all of that turn's prose.
          const nodes: ReactNode[] = [];
          let toolRun: ToolContentSegment[] = [];
          const flushToolRun = (atIndex: number) => {
            if (toolRun.length === 0) return;
            const run = toolRun;
            nodes.push(
              <div key={`tools-${atIndex}`} className="flex flex-col gap-1.5 max-w-[85%]">
                {run.map((seg) => renderToolSegment(seg))}
              </div>,
            );
            toolRun = [];
          };
          renderSegments.forEach((seg, i) => {
            if (seg.kind === 'tool') {
              toolRun.push(seg);
              return;
            }
            flushToolRun(i);
            nodes.push(renderTextSegment(seg, i));
          });
          flushToolRun(renderSegments.length);
          return nodes;
        })()}
        {stopped && <StoppedTurnNote />}
        <SignalCards
          parts={message.parts}
          onSendMessage={onSendMessage}
          originalUserText={originalUserText}
          onStartCall={onStartCall}
          isLastMessage={isLastMessage}
          isStreaming={isStreaming || audioAnswerStreaming}
        />
        {truthGuardReasons.length > 0 && (
          <AmberCautionBanner headline="Truth Guard: couldn’t verify part of this reply">
            <ul className="mt-1 space-y-0.5 list-disc pl-4 text-amber-700/90 dark:text-amber-300/90">
              {truthGuardReasons.map((r, i) => (
                <li key={i}>{r.message}</li>
              ))}
            </ul>
          </AmberCautionBanner>
        )}
        {stallCautionMessages.length > 0 && (
          <AmberCautionBanner headline="EDDY may have stopped before finishing">
            <ul className="mt-1 space-y-0.5 list-disc pl-4 text-amber-700/90 dark:text-amber-300/90">
              {stallCautionMessages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </AmberCautionBanner>
        )}
        {gaveUpReconnecting && (
          <AmberCautionBanner headline={reconnectCopy.headline}>
            <p className="mt-1 text-amber-700/90 dark:text-amber-300/90">{reconnectCopy.body}</p>
          </AmberCautionBanner>
        )}
        <MessageFooter
          message={message}
          sessionId={sessionId}
          isStreaming={audioAnswerStreaming}
          onDelete={onDelete}
          onSaveAsDocument={onSaveAsDocument}
          linkDisabled={disableIdentityLink}
        />
      </div>
    </div>
  );
}
